import React, { useCallback, useEffect } from 'react';
import { Option, TextBlock } from 'neon-design-system';
import { useFormGroup, useManageTemplateForm } from '../../../../utils/form.utils';
import ManageTemplate, { ManageTemplateInstance } from '../../../ui/ManageTemplate';
import { ChannelSubscriptionType } from '../../../../shared/types';
import {
	getPremiumChannelsSubscribersFormBreadCrumbs,
	getPremiumChannelsSubscribersFormCreateButtonLabel,
	getPremiumChannelsSubscribersFormInstance,
	premiumChannelsSubscribersFormDeleteButtonLabel,
	premiumChannelsSubscribersFormDeleteDialogTitle,
	PremiumChannelsSubscribersFormInterface,
	premiumChannelsSubscribersFormUpdateButtonLabel, styles
} from './ChannelSubscribersShared';
import {
	CANCEL_NON_STRIPE_SUBSCRIPTION_IMMEDIATELY,
	CREATE_FREE_CHANNEL_SUBSCRIPTION,
	UPDATE_FREE_CHANNEL_SUBSCRIPTION
} from '../../../../gql/subscriptions';

export const FreeChannelSubscribersForm = (props: PremiumChannelsSubscribersFormInterface) => {
	const classes = styles();

	const {
		channelId,
		userFormControl,
		notesFormControl,
		fileFormControl,
		returnUrl,
		isEdit,
		isBulk,
		channel,
		proceedBulk,
		usersField,
		notesField,
		currentUser,
		onDelete
	} = props;


	useEffect(() => {
		if (isEdit) {
			const cs = currentUser?.channelSubscriptions[0];
			if (!cs) return;

			currentUser && userFormControl.setValue({
				label: `${currentUser.name} (${currentUser.email})`,
				value: currentUser._id
			} as Option);

			notesFormControl.setValue(cs.notes);
		}
	}, [isEdit, channel])

	const addSubscriberEndpoint = () => {
		return CREATE_FREE_CHANNEL_SUBSCRIPTION;
	}

	const editSubscriberEndpoint = () => {
		return UPDATE_FREE_CHANNEL_SUBSCRIPTION;
	}

	const deleteSubscriberEndpoint = () => {
		return CANCEL_NON_STRIPE_SUBSCRIPTION_IMMEDIATELY;
	}

	const form = useFormGroup({
		...(isBulk ? {
			file: fileFormControl
		} : {
			user: userFormControl
		}),
		notes: notesFormControl
	});

	const { isNew, formMessage, formBusy, onSubmit, onCancel, onAlertBoxClose } = useManageTemplateForm(
		ManageTemplateInstance.ChannelSubscriber,
		form,
		isEdit,
		addSubscriberEndpoint(),
		editSubscriberEndpoint(),
		deleteSubscriberEndpoint(),
		returnUrl
	);

	const getReqBody = useCallback(() => {
		const { value } = form;

		return {
			channelId,
			...(value.user ? {userId: value.user} : {}),
			notes: value.notes,
			subscriptionType: ChannelSubscriptionType.Free,
			addedByAdmin: !isBulk ? true : undefined
		};
	}, [form])

	const onBulkProceed = () => {
		form.setDirty();
		if (!form.isValid) return;
		proceedBulk(getReqBody());
	};

	return (
		<ManageTemplate
			pathArea={'Channels'}
			returnUrl={returnUrl}
			templateInstance={getPremiumChannelsSubscribersFormInstance(isBulk)}
			isNew={isNew}
			formMessage={formMessage}
			formBusy={formBusy}
			onCancel={onCancel}
			onSubmit={(ev: any): void => {
				!isBulk ? onSubmit(ev, getReqBody()) : onBulkProceed();
			}}
			onDelete={onDelete}
			onAlertBoxClose={onAlertBoxClose}
			templateCreateButtonLabel={getPremiumChannelsSubscribersFormCreateButtonLabel(isBulk)}
			templateUpdateButtonLabel={premiumChannelsSubscribersFormUpdateButtonLabel}
			templateDeleteButtonLabel={premiumChannelsSubscribersFormDeleteButtonLabel}
			templateDeleteDialogTitle={premiumChannelsSubscribersFormDeleteDialogTitle}
			breadcrumbOverride={getPremiumChannelsSubscribersFormBreadCrumbs(channel.name, returnUrl, isEdit, isBulk)}
			maxContentWidth={isEdit ? 450 : 420}
			helperContent={
				<TextBlock className={classes.helperText}>
					Manually add a user as active subsriber of your channel.
				</TextBlock>
			}
		>
			{usersField}
			{notesField}
		</ManageTemplate>
	)
}
